"use client"

import styled from "@emotion/styled"
import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/he"
import { postWebBmbyLead } from "api"
import { useTranslations } from "next-intl"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { extractProperty, submitClick, submitError, submitSuccess } from "utils"
import { Calendar3Icon, PriceIcon, XIcon } from "../icons"
import {
  StyledDatePicker,
  datePickerSlotProps,
} from "../PortFacilitiesInnerPage/CalendarWithTimeLayout"
import { StyledClearIcon, StyledFieldIcon } from "../PortFacilitiesInnerPage/shared"
import PortFormResultState from "../PortFormResultState"

const StyledCard = styled.div`
  width: 375px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 17.3px 0px rgba(114, 111, 111, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 700px;
  overflow-y: auto;
`

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  flex: 1;
`

const StyledCardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  text-align: center;
  text-transform: capitalize;
  width: 100%;
`

const StyledSectionLabel = styled.span`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
`

const StyledFieldRow = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    background-color: white;
    border: 1.2px solid #e6e6e6;
    border-radius: 8px;
    height: 48px;

    &:before,
    &:after {
      display: none !important;
    }

    &:hover {
      background-color: white;
    }

    &.Mui-focused {
      background-color: white;
      border-color: #0e2846;
    }

    &.Mui-error {
      border-color: #c92418;
    }
  }

  .MuiInputBase-input {
    padding: 12px;
    font-size: 16px;
    font-weight: 300;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputLabel-root {
    position: relative;
    transform: none;
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
    font-size: 16px;
    font-weight: 300;
    line-height: 1.5;
    margin-bottom: 4px;
    pointer-events: auto;

    &.Mui-focused {
      color: ${({ theme }) => theme.colors.port.defaultTextColor};
    }
  }

  .MuiFormHelperText-root {
    font-size: 14px;
    margin-inline-start: 0;
  }
`

const StyledCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledCheckboxLabel = styled(FormControlLabel)`
  align-items: flex-start !important;
  margin: 0 !important;
  gap: 10px;

  .MuiCheckbox-root {
    padding: 0;
  }

  .MuiTypography-root {
    font-size: 16px;
    font-weight: 300;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
  }
`

const StyledCheckbox = styled(Checkbox)`
  && {
    width: 24px;
    height: 24px;
    border-radius: 4.8px;
    border: 1.2px solid #e6e6e6;
    background: white;
    flex-shrink: 0;

    &.Mui-checked {
      color: #0000fa;
    }
  }
`

const StyledStickyFooter = styled.div`
  padding: 24px 24px 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 10%);
  position: sticky;
  bottom: 0;
`

const StyledCTAButton = styled(Button)<{ $active: boolean }>`
  && {
    width: 100%;
    height: 46px;
    border-radius: 45px;
    font-size: 18px;
    font-weight: 700;
    text-transform: none;
    text-decoration: none;
    box-shadow: none;
    background-color: ${({ $active, theme }) =>
      $active ? theme.colors.propBlue : theme.colors.port.portBulletColor};
    color: ${({ theme }) => theme.colors.white};
    cursor: ${({ $active }) => ($active ? "pointer" : "default")};

    &:hover {
      background-color: ${({ $active, theme }) =>
        $active ? theme.colors.buttonHoverBackgroundColor : theme.colors.port.portBulletColor};
      box-shadow: none;
      text-decoration: none;
    }
  }
`


const StyledPriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  span {
    font-size: 18px;
    font-weight: 300;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledDateFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const StyledDateFieldLabel = styled.span`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
`

type ContactCardProps = {
  data?: any
  companyId?: number
  variant?: "card" | "inline"
  showPrice?: boolean
  showDatePicker?: boolean
  price?: string
  companySectionLabel?: string
  mediaTitle?: string
}

export default function ContactCard({
  data,
  companyId,
  variant = "card",
  showPrice,
  showDatePicker,
  price,
  companySectionLabel,
  mediaTitle,
}: ContactCardProps) {
  const t = useTranslations("portMeetingRoomProduct")
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const [fullName, setFullName] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [allowMarketing, setAllowMarketing] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [date, setDate] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [submitState, setSubmitState] = useState<"idle" | "loading" | "success" | "fail">("idle")
  const [errors, setErrors] = useState<Record<string, boolean>>({})

  const bmbyID = data ? extractProperty(data, "bmbyID") : null
  const emailForLead = data ? extractProperty(data, "emailForLead") || "" : ""

  const validate = () => {
    const newErrors: Record<string, boolean> = {}
    if (!fullName.trim()) newErrors.fullName = true
    if (!phone.trim()) newErrors.phone = true
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) newErrors.email = true
    if (!companyName.trim()) newErrors.companyName = true
    if (!acceptTerms) newErrors.acceptTerms = true
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    submitClick("שליחה", "לתיאום סיור")

    const recaptchaResponse = await recaptchaRef.current?.executeAsync()
    recaptchaRef.current?.reset()

    if (!recaptchaResponse) {
      setSubmitState("fail")
      submitError("שליחה", "לתיאום סיור", "reCAPTCHA response is empty")
      return
    }

    try {
      setSubmitState("loading")
      const senddata = {
        projectId: bmbyID || -1,
        name: fullName,
        email,
        phone,
        comments: [
          `שם החברה: ${companyName}`,
          date ? `תאריך מועדף: ${dayjs(date).format("DD/MM/YYYY")}` : "",
        ].filter(Boolean).join(" | "),
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "",
        allowedMarketing: allowMarketing,
        allowedSmsAndEmail: allowMarketing,
        companyId: companyId || -1,
        uri: window?.location?.href,
        toEmail: emailForLead,
        intIn: "",
        mIkum: "",
        ...(mediaTitle ? { mediaTitle } : {}),
      }

      const response = await postWebBmbyLead(senddata)

      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", "לתיאום סיור", true)
        setSubmitState("success")
        setSubmitted(true)
      } else {
        setSubmitState("fail")
        submitError("שליחה", "לתיאום סיור", response?.data?.body?.errorMessage)
      }
    } catch (error) {
      setSubmitState("fail")
      submitError("שליחה", "לתיאום סיור", error as any)
    }
  }

  const isActive = !!fullName && !!phone && !!email && !!companyName && acceptTerms

  const Wrapper = variant === "card" ? StyledCard : "div"

  const handleReset = () => {
    setSubmitted(false)
    setSubmitState("idle")
    setFullName("")
    setPhone("")
    setEmail("")
    setCompanyName("")
    setAllowMarketing(false)
    setAcceptTerms(false)
    setErrors({})
  }

  if (submitted && (submitState === "success" || submitState === "fail")) {
    return (
      <Wrapper>
        <PortFormResultState
          isSuccess={submitState === "success"}
          title={submitState === "success" ? t("successTitle") : t("errorTitle")}
          subtitle={submitState === "success" ? t("successSubtitle") : t("errorSubtitle")}
          buttonText={submitState === "success" ? t("backToHome") : t("tryAgain")}
          onButtonClick={
            submitState === "success"
              ? () => (window.location.href = "/")
              : handleReset
          }
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <StyledCardContent>
        <StyledCardTitle>{t("scheduleTour")}</StyledCardTitle>

        {showPrice && price && (
          <StyledPriceRow>
            <span>{t("priceFrom")} {price} {t("perHour")}</span>
            <PriceIcon />
          </StyledPriceRow>
        )}

        {showDatePicker && (
          <StyledDateFieldWrapper>
            <StyledDateFieldLabel>{t("when")}</StyledDateFieldLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
              <div style={{ position: "relative" }}>
                <StyledDatePicker
                  value={date ? dayjs(date) : null}
                  onChange={(newDate: any) => {
                    setDate(newDate ? (newDate as Dayjs).format("YYYY-MM-DD") : "")
                  }}
                  minDate={dayjs()}
                  format="DD/MM/YYYY"
                  slots={{ openPickerIcon: () => null }}
                  slotProps={datePickerSlotProps}
                />
                {date ? (
                  <StyledClearIcon
                    onMouseDown={(e: any) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setDate("")
                    }}
                  >
                    <XIcon color="#0e2846" />
                  </StyledClearIcon>
                ) : (
                  <StyledFieldIcon>
                    <Calendar3Icon color="#687079" />
                  </StyledFieldIcon>
                )}
              </div>
            </LocalizationProvider>
          </StyledDateFieldWrapper>
        )}

        <StyledSectionLabel>{t("contactDetails")}</StyledSectionLabel>
        <StyledFieldRow>
          <StyledTextField
            variant="standard"
            label={`*${t("phone")}`}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value)
              setErrors((prev) => ({ ...prev, phone: false }))
            }}
            error={errors.phone}
            helperText={errors.phone ? t("required") : ""}
            fullWidth
          />
          <StyledTextField
            variant="standard"
            label={`*${t("fullName")}`}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value)
              setErrors((prev) => ({ ...prev, fullName: false }))
            }}
            error={errors.fullName}
            helperText={errors.fullName ? t("required") : ""}
            fullWidth
          />
        </StyledFieldRow>
        <StyledTextField
          variant="standard"
          label={`*${t("email")}`}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setErrors((prev) => ({ ...prev, email: false }))
          }}
          error={errors.email}
          helperText={errors.email ? t("required") : ""}
          fullWidth
          type="email"
        />

        <StyledSectionLabel>{companySectionLabel || t("companyDetails")}</StyledSectionLabel>
        <StyledTextField
          variant="standard"
          label={`*${t("companyName")}`}
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value)
            setErrors((prev) => ({ ...prev, companyName: false }))
          }}
          error={errors.companyName}
          helperText={errors.companyName ? t("required") : ""}
          fullWidth
        />

        <StyledCheckboxGroup>
          <StyledCheckboxLabel
            control={
              <StyledCheckbox
                checked={allowMarketing}
                onChange={(e) => setAllowMarketing(e.target.checked)}
              />
            }
            label={t("marketingConsent")}
          />
          <StyledCheckboxLabel
            control={
              <StyledCheckbox
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked)
                  setErrors((prev) => ({ ...prev, acceptTerms: false }))
                }}
              />
            }
            label={t("privacyConsent")}
          />
        </StyledCheckboxGroup>

        <ReCAPTCHA
          ref={recaptchaRef as LegacyRef<ReCAPTCHA>}
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY || process.env.RECAPTCHA_KEY || ""}
          size="invisible"
        />
      </StyledCardContent>

      <StyledStickyFooter>
        <StyledCTAButton
          $active={isActive}
          onClick={handleSubmit}
          variant="contained"
          disableElevation
          disabled={submitState === "loading"}
        >
          {submitState === "loading" ? "..." : t("submitForm")}
        </StyledCTAButton>
      </StyledStickyFooter>
    </Wrapper>
  )
}
